import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { checkVideoCallPackage } from '../../utils/videoCallUtils';

const VideoCallUsageBadge = ({ userId, showFullInfo = false, size = 'sm' }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [packageInfo, setPackageInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const targetUserId = userId || currentUser?.uid;

  useEffect(() => {
    if (targetUserId) {
      loadPackageInfo();
    }
  }, [targetUserId]);

  const loadPackageInfo = async () => {
    try {
      setLoading(true);
      const info = await checkVideoCallPackage(targetUserId);
      setPackageInfo(info);
    } catch (error) {
      console.error('Error loading video call package info:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-1">
        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
        <span className="text-xs text-gray-500">{t('common.loading')}</span>
      </div>
    );
  }

  if (!packageInfo?.hasPackage) {
    return (
      <div className={`flex items-center gap-1 ${size === 'sm' ? 'text-xs' : 'text-sm'} text-gray-500`}>
        <span>⚠️</span>
        <span>{t('videoCall.noPackage')}</span>
      </div>
    );
  }

  const { callsLimit, callsUsed, callsRemaining, packageType } = packageInfo;
  const isUnlimited = callsLimit === -1;
  const percentage = isUnlimited ? 100 : callsLimit > 0 ? (callsUsed / callsLimit) * 100 : 0;

  // Badge style based on remaining
  const getBadgeStyle = () => {
    if (isUnlimited) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    if (callsRemaining === 0) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    if (callsRemaining <= 3) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  // Compact version (default)
  if (!showFullInfo) {
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${getBadgeStyle()} ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
        {isUnlimited ? (
          <>
            <span>♾️</span>
            <span>{t('videoCall.unlimited')}</span>
          </>
        ) : (
          <>
            <span>📹</span>
            <span>{t('videoCall.remaining', { remaining: callsRemaining, limit: callsLimit })}</span>
          </>
        )}
      </div>
    );
  }

  // Full info version
  return (
    <div className={`border rounded-lg p-3 ${getBadgeStyle()}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">📹</span>
          <div>
            <div className={`font-semibold ${size === 'sm' ? 'text-sm' : 'text-base'}`}>
              {t('videoCall.package')}: {packageType?.toUpperCase() || 'N/A'}
            </div>
            {isUnlimited ? (
              <div className="text-xs text-green-700 mt-1">
                {t('videoCall.unlimitedCalls')}
              </div>
            ) : (
              <div className="text-xs mt-1">
                {t('videoCall.used', { used: callsUsed, limit: callsLimit })}
              </div>
            )}
          </div>
        </div>
        {!isUnlimited && (
          <div className="text-right">
            <div className={`font-bold ${size === 'sm' ? 'text-lg' : 'text-xl'}`}>
              {callsRemaining}
            </div>
            <div className="text-xs">{t('videoCall.remainingCalls')}</div>
          </div>
        )}
      </div>
      
      {!isUnlimited && (
        <div className="mt-2">
          <div className="flex items-center justify-between text-xs mb-1">
            <span>{t('videoCall.usage')}</span>
            <span>{callsUsed}/{callsLimit} ({Math.round(percentage)}%)</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                callsRemaining === 0
                  ? 'bg-red-500'
                  : callsRemaining <= 3
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCallUsageBadge;




